// frontend/src/components/Diario/ChecklistPrioritario.jsx
// Componente principal: Lista de Tarefas (Checklist Prioritário)
// Foco visual principal com botões de check grandes e fáceis de clicar

import React, { useMemo } from 'react';
import { CheckCircle2, Circle, Clock, AlertTriangle, TrendingUp, Leaf } from 'lucide-react';
import TarefaCardMinimal from './TarefaCardMinimal';
import { parseLocalDateTime } from '../../utils/formatters';

/**
 * Componente de Checklist Prioritário
 * Agrupa tarefas por horários ou categorias e mostra progresso
 * 
 * @param {array} tarefas - Lista de tarefas pendentes
 * @param {function} onConcluir - Handler para concluir tarefa
 */
const ChecklistPrioritario = ({ tarefas, onConcluir }) => {
    const agora = new Date();

    // Agrupa tarefas por status
    const { tarefasAtrasadas, tarefasPendentes, tarefasConcluidas } = useMemo(() => {
        const atrasadas = [];
        const pendentes = [];
        const concluidas = [];

        tarefas.forEach(t => {
            if (t.status === 'Concluída') {
                concluidas.push(t);
                return;
            }

            if (!t.hora_agendada) {
                if (t.status === 'Atrasada') {
                    atrasadas.push(t);
                } else {
                    pendentes.push(t);
                }
                return;
            }

            // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
            const horaAgendada = parseLocalDateTime(t.hora_agendada);
            if (!horaAgendada) {
                if (t.status === 'Atrasada') {
                    atrasadas.push(t);
                } else {
                    pendentes.push(t);
                }
                return;
            }
            const diferencaMinutos = (agora - horaAgendada) / (1000 * 60);

            if (diferencaMinutos > 15) {
                atrasadas.push(t);
            } else {
                pendentes.push(t);
            }
        });

        // Ordena por horário
        const ordenarPorHorario = (a, b) => {
            const dateA = a.hora_agendada ? (parseLocalDateTime(a.hora_agendada) || new Date(0)) : new Date(0);
            const dateB = b.hora_agendada ? (parseLocalDateTime(b.hora_agendada) || new Date(0)) : new Date(0);
            return dateA - dateB;
        };

        return {
            tarefasAtrasadas: atrasadas.sort(ordenarPorHorario),
            tarefasPendentes: pendentes.sort(ordenarPorHorario),
            tarefasConcluidas: concluidas.sort(ordenarPorHorario)
        };
    }, [tarefas, agora]);

    // Calcula progresso
    // Nota: Se o hook retornar apenas tarefas pendentes, não teremos concluídas
    // Nesse caso, o progresso será baseado apenas nas tarefas atrasadas vs pendentes
    const totalTarefas = tarefas.length;
    const tarefasConcluidasCount = tarefasConcluidas.length;
    const tarefasPendentesCount = tarefasPendentes.length;
    
    // Se houver tarefas concluídas, calcula progresso normalmente
    // Caso contrário, mostra apenas o número de pendentes
    const percentualConcluido = totalTarefas > 0 && tarefasConcluidasCount > 0
        ? Math.round((tarefasConcluidasCount / totalTarefas) * 100) 
        : 0;

    // Agrupa tarefas por turno (Manhã, Tarde, Noite)
    const agruparPorTurno = (listaTarefas) => {
        const grupos = {
            'Manhã': [],
            'Tarde': [],
            'Noite': [],
            'Madrugada': [],
            'Sem Horário': []
        };

        listaTarefas.forEach(tarefa => {
            if (!tarefa.hora_agendada) {
                grupos['Sem Horário'].push(tarefa);
                return;
            }

            // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
            const hora = parseLocalDateTime(tarefa.hora_agendada);
            if (!hora) {
                grupos['Sem Horário'].push(tarefa);
                return;
            }
            const horaNum = hora.getHours();

            if (horaNum >= 6 && horaNum < 12) {
                grupos['Manhã'].push(tarefa);
            } else if (horaNum >= 12 && horaNum < 18) {
                grupos['Tarde'].push(tarefa);
            } else if (horaNum >= 18 && horaNum < 24) {
                grupos['Noite'].push(tarefa);
            } else {
                grupos['Madrugada'].push(tarefa);
            }
        });

        return grupos;
    };

    const tarefasAtrasadasPorTurno = agruparPorTurno(tarefasAtrasadas);
    const tarefasPendentesPorTurno = agruparPorTurno(tarefasPendentes);

    return (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100">
            {/* Header com Indicador de Progresso */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">
                        📋 Checklist de Tarefas
                    </h2>
                    <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 rounded-full">
                        <TrendingUp size={16} className="text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-700">
                            {tarefasConcluidasCount > 0 
                                ? `${tarefasConcluidasCount} de ${totalTarefas} Concluídas`
                                : `${tarefasPendentesCount} Pendentes`
                            }
                        </span>
                    </div>
                </div>

                {/* Barra de Progresso - Só mostra se houver tarefas concluídas */}
                {totalTarefas > 0 && tarefasConcluidasCount > 0 && (
                    <div className="w-full">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                            <span>Progresso do Plantão</span>
                            <span className="font-semibold">{percentualConcluido}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                                className="bg-emerald-500 h-full transition-all duration-500 ease-out rounded-full"
                                style={{ width: `${percentualConcluido}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Conteúdo do Checklist */}
            <div className="p-6">
                {totalTarefas === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-500 opacity-50" />
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Leaf size={20} className="text-emerald-500" />
                            <p className="font-semibold text-lg">Nenhuma tarefa pendente!</p>
                        </div>
                        <p className="text-sm mt-2">Parabéns! Todas as tarefas foram concluídas.</p>
                    </div>
                ) : (
                    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
                        {/* Tarefas Atrasadas */}
                        {tarefasAtrasadas.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <AlertTriangle size={20} className="text-red-600" />
                                    <h3 className="text-lg font-semibold text-red-800">
                                        ⚠️ Atrasadas ({tarefasAtrasadas.length})
                                    </h3>
                                </div>
                                <div className="space-y-3">
                                    {Object.entries(tarefasAtrasadasPorTurno).map(([turno, tarefasTurno]) => {
                                        if (tarefasTurno.length === 0) return null;
                                        return (
                                            <div key={`atrasadas-${turno}`} className="space-y-2">
                                                {turno !== 'Sem Horário' && (
                                                    <h4 className="text-sm font-medium text-gray-600 ml-2">
                                                        {turno}
                                                    </h4>
                                                )}
                                                {tarefasTurno.map(tarefa => (
                                                    <TarefaCardMinimal
                                                        key={tarefa.id}
                                                        tarefa={tarefa}
                                                        onConcluir={onConcluir}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Tarefas Pendentes */}
                        {tarefasPendentes.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <Clock size={20} className="text-blue-600" />
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        Pendentes ({tarefasPendentes.length})
                                    </h3>
                                </div>
                                <div className="space-y-3">
                                    {Object.entries(tarefasPendentesPorTurno).map(([turno, tarefasTurno]) => {
                                        if (tarefasTurno.length === 0) return null;
                                        return (
                                            <div key={`pendentes-${turno}`} className="space-y-2">
                                                {turno !== 'Sem Horário' && (
                                                    <h4 className="text-sm font-medium text-gray-600 ml-2">
                                                        {turno}
                                                    </h4>
                                                )}
                                                {tarefasTurno.map(tarefa => (
                                                    <TarefaCardMinimal
                                                        key={tarefa.id}
                                                        tarefa={tarefa}
                                                        onConcluir={onConcluir}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChecklistPrioritario;

